import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue } from "framer-motion";
import EmailRegistrationModal from "./components/forms/EmailRegistrationModal";
import useEmailRegistration from "./hooks/useEmailRegistration";
import CandidateCard from "./components/cards/CandidateCard";

export default function Homepage() {
    const { showRegistrationModal, openRegistrationModal, closeRegistrationModal } = useEmailRegistration();
    const constraintsRef = useRef(null);
    const carouselRef = useRef(null);
    const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
    const x = useMotionValue(0);
    const [isHovering, setIsHovering] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const autoScrollInterval = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [reviews, setReviews] = useState([
        {
            id: 1,
            image: "/asset/ForJobSeekers/1.png",
            quote: "I had to switch jobs quickly due to personal circumstances, and Hiree made the process incredibly easy. I got multiple offers within 24 hours! Hiree helped me connect with a great employer, and I've been working here for 3 months now. Highly recommend it!",
            author: "Aaron M."
        },
        {
            id: 2,
            image: "/asset/ForJobSeekers/2.png",
            quote: "Hiree's platform is incredibly user-friendly. I found my dream job at a local café within days. The application process was smooth, and the team was very supportive throughout.",
            author: "Sarah L."
        },
        {
            id: 3,
            image: "/asset/ForJobSeekers/3.png",
            quote: "As someone new to the hospitality industry, Hiree made it easy to find entry-level positions. The detailed job descriptions and direct communication with employers were incredibly helpful.",
            author: "Michael R."
        }
    ]);

    const candidates = useMemo(() => [
        {
            name: "Derek Anderson",
            experience: "5 Years of sales experience",
            recommendations: "3 recommendations",
            image: "/asset/HomePage/1.png"
        },
        {
            name: "Farah Haddad",
            experience: "5 Years of hospitality experience",
            recommendations: "3 recommendations",
            image: "/asset/HomePage/2.png"
        },
        {
            name: "Mia Sheikh",
            experience: "2 Years of retail experience",
            recommendations: "2 recommendations",
            image: "/asset/HomePage/3.png"
        },
        {
            name: "Michael Miller",
            experience: "12 Years of management experience",
            recommendations: "7 recommendations",
            image: "/asset/HomePage/4.png"
        },
        {
            name: "Laura Chen",
            experience: "3 Years of hospitality experience",
            recommendations: "4 recommendations",
            image: "/asset/HomePage/5.png"
        },
        {
            name: "James Wilson",
            experience: "4 Years of service experience",
            recommendations: "5 recommendations",
            image: "/asset/HomePage/6.png"
        }
    ], []);

    // 动态计算拖动约束
    useEffect(() => {
        const updateConstraints = () => {
            if (constraintsRef.current && carouselRef.current) {
                const containerWidth = constraintsRef.current.clientWidth;
                const carouselWidth = carouselRef.current.scrollWidth;
                
                setDragConstraints({
                    left: -(carouselWidth - containerWidth),
                    right: 0
                });
            }
        };

        // Initial calculation
        updateConstraints();

        // Listen for window resize
        window.addEventListener('resize', updateConstraints);
        
        // Cleanup listener
        return () => window.removeEventListener('resize', updateConstraints);
    }, []); // Remove candidates dependency

    // Auto-scrolling effect with infinite looping
    useEffect(() => {
        const startAutoScroll = () => {
            if (autoScrollInterval.current) clearInterval(autoScrollInterval.current);
            
            autoScrollInterval.current = setInterval(() => {
                if (isHovering || isDragging) return;
                
                if (carouselRef.current && constraintsRef.current) {
                    const currentPosition = x.get();
                    const containerWidth = constraintsRef.current.clientWidth;
                    const carouselWidth = carouselRef.current.scrollWidth;
                    const maxScroll = -(carouselWidth - containerWidth);
                    
                    // Move by 1px each interval for smooth animation
                    let newPosition = currentPosition - 1;
                    
                    // When we reach the end, reset to beginning with a smooth transition
                    if (newPosition < maxScroll + 50) {
                        // Adding a small buffer (50px) before reset
                        newPosition = 0;
                    }
                    
                    x.set(newPosition);
                }
            }, 20); // Update every 20ms for smooth motion
        };
        
        startAutoScroll();
        
        return () => {
            if (autoScrollInterval.current) {
                clearInterval(autoScrollInterval.current);
            }
        };
    }, [isHovering, isDragging, x]);

    const handleDragEnd = (event, info) => {
        const currentX = x.get();
        const containerWidth = constraintsRef.current?.clientWidth || 0;
        const carouselWidth = constraintsRef.current?.firstChild?.scrollWidth || 0;
        
        // 计算最大可拖动距离（负值）
        const maxDrag = Math.min(0, -(carouselWidth - containerWidth));
        
        // 计算新位置
        let newX = currentX + info.offset.x;
        
        // 限制在有效范围内
        if (newX > 0) {
            newX = 0;  // 右边界
        } else if (newX < maxDrag) {
            newX = maxDrag;  // 左边界
        }
        
        // 设置新位置
        x.set(newX);
    };

    const handlePrevSlide = () => {
        setCurrentSlide((currentSlide - 1 + reviews.length) % reviews.length);
    };

    const handleNextSlide = () => {
        setCurrentSlide((currentSlide + 1) % reviews.length);
    };

    return (
        <div className="min-h-screen bg-white">

            {/* Hero Section */}
            <div className="relative bg-[#FFF5EB]">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `linear-gradient(45deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 20%, rgba(255, 140, 0, 0.65) 100%), url('/asset/HomePage/backGroundImage.png')`,
                        borderRadius: '0 0 40px 40px'
                    }}
                />
                <div className="relative max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center">
                    <div className="md:w-1/2 mb-8 md:mb-0 text-left">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            <div>Join, Find, Work –</div>
                            <div>Great Jobs Made Simple with AI</div>
                        </h1>
                        <p className="text-gray-600 mb-8">
                            Connecting Businesses with Ready-to-Work Talent, Instantly.
                        </p>
                        <Link
                            to="/signup"
                            className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90"
                        >
                            Register Now
                        </Link>
                    </div>
                    <div className="md:w-1/2 flex justify-center">
                        <img src="/asset/HomePage/mobileExample.png" alt="Mobile App" className="h-96 object-contain relative z-10" />
                    </div>
                </div>

                {/* Statistics Box */}
                <div className="max-w-4xl mx-auto -mb-100 bg-white rounded-xl shadow-lg p-6 relative z-20">
                    <div className="flex justify-between items-center">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-[#F47521]">2,500<span className="text-gray-400 text-lg">+</span></div>
                            <div className="text-gray-600 text-sm">Job seekers</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-[#F47521]">250<span className="text-gray-400 text-lg">+</span></div>
                            <div className="text-gray-600 text-sm">Businesses</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-[#F47521]">800<span className="text-gray-400 text-lg">+</span></div>
                            <div className="text-gray-600 text-sm">Job posted</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-[#F47521]">87<span className="text-gray-400 text-lg">%</span></div>
                            <div className="text-gray-600 text-sm">Success matches</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-[#F47521]">24/7</div>
                            <div className="text-gray-600 text-sm">Job match/hiring</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Candidate Carousel Section */}
            <section className="py-16 bg-gradient-to-r from-[#FFB6B6] to-[#B987E8]">
                <div className="max-w-6xl mx-auto px-6">
                    {/* Section Title */}
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-white flex items-center gap-2 mb-4">
                            <div className="flex items-center gap-1">
                                <span className="text-yellow-200 text-xl">★</span>
                                <span className="text-yellow-200 text-3xl">★</span>
                            </div>
                            Say Hi To Your Next Team Player
                            <div className="flex items-center gap-1">
                                <span className="text-yellow-200 text-3xl">★</span>
                                <span className="text-yellow-200 text-xl">★</span>
                            </div>
                        </h2>
                        <p className="text-white text-left">
                            Find skilled and reliable professionals on Hiree, ready to start working today.
                        </p>
                    </div>

                    {/* Carousel */}
                    <div className="relative overflow-hidden w-full" ref={constraintsRef}>
                        <motion.div 
                            ref={carouselRef}
                            className="flex cursor-grab active:cursor-grabbing"
                            drag="x"
                            dragConstraints={dragConstraints}
                            dragElastic={0.1}
                            dragMomentum={false}
                            onHoverStart={() => setIsHovering(true)}
                            onHoverEnd={() => setIsHovering(false)}
                            onDragStart={() => setIsDragging(true)}
                            onDragEnd={() => setIsDragging(false)}
                            style={{ x, touchAction: 'none' }}
                        >
                            {/* Display candidate cards with animations */}
                            {candidates.map((candidate, index) => {
                                return (
                                    <motion.div
                                        key={`candidate-${index}`}
                                        className="flex-shrink-0 mx-2"
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <CandidateCard
                                            image={candidate.image}
                                            name={candidate.name}
                                            experience={candidate.experience}
                                            recommendations={candidate.recommendations}
                                            candidateId={`candidate-${index}`}
                                            isStarCandidate={index % 3 === 0} // Make every third candidate a star candidate
                                        />
                                    </motion.div>
                                );
                            })}
                            
                            {/* Duplicate set for infinite scrolling */}
                            {candidates.map((candidate, index) => {
                                return (
                                    <motion.div
                                        key={`duplicate-${index}`}
                                        className="flex-shrink-0 mx-2"
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <CandidateCard
                                            image={candidate.image}
                                            name={candidate.name}
                                            experience={candidate.experience}
                                            recommendations={candidate.recommendations}
                                            candidateId={`candidate-dup-${index}`}
                                            isStarCandidate={index % 3 === 0} // Keep the same pattern in duplicates
                                        />
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Email Registration Modal */}
            <EmailRegistrationModal 
                show={showRegistrationModal} 
                onClose={closeRegistrationModal} 
            />

            {/* Clients Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl font-bold mb-4">Our Clients</h2>
                        <p className="text-gray-600">
                            We have been working with some Fortune 500+ restaurants
                        </p>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-7 gap-8">
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo1.png" alt="Client Logo 1" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo2.png" alt="Client Logo 2" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo3.png" alt="Client Logo 3" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo4.png" alt="Client Logo 4" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo5.png" alt="Client Logo 5" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo6.png" alt="Client Logo 6" className="w-20 h-12 object-contain" />
                        </div>
                        <div className="flex items-center justify-center">
                            <img src="/asset/HomePage/clientLogo3.png" alt="Client Logo 3" className="w-20 h-12 object-contain" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Community Section */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl font-bold">Join our community today</h2>
                        <p className="text-gray-600">Who is Table checker suitable for?</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: "/asset/HomePage/jobSeekerLogo.png",
                                title: "Job seekers",
                                description: "Designed for hospitality workers seeking flexible, short-term gigs, offering insight into workplace reputations and verified feedback to help find ideal shifts at trusted locations."
                            },
                            {
                                icon: "/asset/HomePage/restaurantLogo.png",
                                title: "Restaurant owners",
                                description: "Perfect for restaurant managers seeking quick, reliable access to quality staff, with centralized reference checks and verified reviews for smarter hiring decisions."
                            },
                            {
                                icon: "/asset/HomePage/studentLogo.png",
                                title: "Students",
                                description: "Ideal for students looking for flexible, part-time gigs that fit around their schedules. Hiree offers insight into workplace reputations and verified reviews."
                            }
                        ].map((item, i) => (
                            <div key={i} className="p-6 bg-white rounded-lg shadow-lg text-center">
                                <div className="w-16 h-16 mb-4 bg-[#FFF5EB] rounded-lg p-3 mx-auto">
                                    <img src={item.icon} alt={item.title} className="w-full h-full object-contain" />
                                </div>
                                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                                <p className="text-gray-600 mb-4">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-8">
                        <Link 
                            to="/signup"
                            className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90">
                            Join in
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features Top Section */}
            <section className="py-16 px-6 bg-[#FFF5EB]">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row gap-12 items-start">
                        <div className="md:w-1/2">
                            <img 
                                src="/asset/HomePage/why table checker.jpg" 
                                alt="Features" 
                                className="w-full h-auto rounded-2xl"
                                style={{ 
                                    height: '320px', 
                                    width: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-bold mb-4">
                                Why Hiree is the best platform for hire and job seeking.
                            </h2>
                            <p className="text-gray-600">
                                Hiree is the ultimate platform for connecting hospitality workers and restaurants, offering verified reviews, streamlined reference management, and tailored job opportunities. Designed for high-turnover industries, it empowers job seekers to find flexible, trusted gigs while enabling managers to hire with confidence. Hiree is redefining how the hospitality industry works—making hiring and job-seeking simple, reliable, and impactful.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Cards Section */}
            <section className="py-16 px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                            <div className="bg-[#F47521] p-4">
                                <h3 className="text-xl font-bold text-white text-left">1. Streamlined Reference Management</h3>
                            </div>
                            <div className="bg-[#FFF5EB] p-6 h-full">
                                <p className="text-gray-600 text-left">
                                    Hiree centralizes reference checks, keeping them confidential until needed, simplifying the hiring process for both managers and workers.
                                </p>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                            <div className="bg-[#F47521] p-4">
                                <h3 className="text-xl font-bold text-white text-left">2. Tailored for High-Turnover Industries</h3>
                            </div>
                            <div className="bg-[#FFF5EB] p-6 h-full">
                                <p className="text-gray-600 text-left">
                                    Unlike generic platforms, Hiree is built specifically for high-turnover sectors, where verified manager feedback is more valuable than traditional resumes.
                                </p>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                            <div className="bg-[#F47521] p-4">
                                <h3 className="text-xl font-bold text-white text-left">3. Lower Turnover Support</h3>
                            </div>
                            <div className="bg-[#FFF5EB] p-6 h-full">
                                <p className="text-gray-600 text-left">
                                    By empowering managers with better insights and helping workers find reputable workplaces, Hiree indirectly contributes to lower turnover rates.
                                </p>
                            </div>
                        </div>
                        <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                            <div className="bg-[#F47521] p-4">
                                <h3 className="text-xl font-bold text-white text-left">4. Reliable References</h3>
                            </div>
                            <div className="bg-[#FFF5EB] p-6 h-full">
                                <p className="text-gray-600 text-left">
                                    Verified reviews from past managers ensure credible, unbiased feedback, offering a trustworthy alternative to unverified review sites.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonial Section */}
            <section className="bg-[#FFF5EB] py-16 px-6 relative overflow-hidden">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8 relative">
                        <div className="md:w-1/2 relative">
                            <img
                                src={reviews[currentSlide].image}
                                alt="Testimonial"
                                className="w-full h-auto rounded-lg"
                            />
                        </div>
                        <div className="md:w-1/2 relative">
                            {/* Navigation Buttons */}
                            <button 
                                onClick={handlePrevSlide}
                                className="absolute left-[-30px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                            >
                                <img src="/asset/HomePage/button-right.png" alt="Previous" className="w-6 h-6 rotate-180" />
                            </button>
                            <button 
                                onClick={handleNextSlide}
                                className="absolute right-[-30px] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                            >
                                <img src="/asset/HomePage/button-right.png" alt="Next" className="w-6 h-6" />
                            </button>

                            {/* Testimonial Content */}
                            <div className="relative min-h-[200px]">
                                <blockquote className="text-gray-700 italic mb-4 text-lg transition-opacity duration-500">
                                    {reviews[currentSlide].quote}
                                </blockquote>
                                <p className="font-bold text-xl transition-opacity duration-500">
                                    - {reviews[currentSlide].author}
                                </p>
                            </div>
                        </div>

                        {/* Dots Indicator */}
                        <div className="absolute bottom-[-20px] left-1/2 transform -translate-x-1/2 flex gap-2">
                            {reviews.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        index === currentSlide ? 'bg-[#FF8C00]' : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-6 bg-gray-50">
                <div className="max-w-6xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="md:w-1/3 mb-8 md:mb-0">
                            <h2 className="text-left text-4xl font-bold mb-2">
                                Helping local
                            </h2>
                            <h2 className="text-left text-4xl font-bold text-[#FF8C00] mb-4">
                                business and people
                            </h2>
                            <p className="text-left text-gray-600">We reached here with our hard work and dedication</p>
                        </div>
                        <div className="flex flex-col md:flex-row gap-16">
                            <div className="flex items-center gap-4">
                                <img src="/asset/HomePage/twoPeopleIcon.png" alt="Members" className="w-12 h-12 flex-shrink-0" />
                                <div>
                                    <div className="text-4xl font-bold">3,200</div>
                                    <div className="text-gray-600">Members</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <img src="/asset/HomePage/fingerIcon.png" alt="Jobs" className="w-12 h-12" />
                                <div>
                                    <div className="text-4xl font-bold">1,828</div>
                                    <div className="text-gray-600">Jobs hired</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}